import React, { useState, useEffect } from 'react';
import { RefreshCw, Search, Filter, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import MilestoneTreeRow from '../../components/milestone/MilestoneTreeRow';
import EvidenceConfigModal from '../../components/milestone/EvidenceConfigModal';
import { getTemplateTree } from '../../services/milestoneService';

/**
 * MilestoneTemplateList Page
 * Main page for viewing milestone templates by project type
 * Displays hierarchical tree structure with evidence configuration
 * 
 * UPDATED: Added TOWER_COLOCATION project type support
 */
const MilestoneTemplateList = () => {
  // State management
  const [selectedProjectType, setSelectedProjectType] = useState('FO');
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'flat'
  
  // Modal state
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  
  // Statistics
  const [stats, setStats] = useState({
    totalMilestones: 0,
    level0Count: 0,
    level1Count: 0,
    level2Count: 0,
    groupCount: 0,
    leafCount: 0,
    totalBobot: 0
  });

  // ✅ UPDATED: Added TOWER_COLO project type
  const projectTypes = [
    { value: 'FO', label: 'FO', color: 'bg-green-600', description: 'Fiber Optic' },
    { value: 'TOWER', label: 'TOWER', color: 'bg-blue-600', description: 'Tower SITAC/CME' },
    { value: 'TOWER_COLO', label: 'TOWER (Colo)', color: 'bg-purple-600', description: 'Tower Colocation' }
  ];

  // Load templates on mount and when project type changes
  useEffect(() => {
    loadTemplates();
  }, [selectedProjectType]);

  // Filter templates when search query changes
  useEffect(() => {
    filterTemplates();
  }, [searchQuery, templates]);

  // Load templates from API
  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await getTemplateTree(selectedProjectType);
      setTemplates(data);
      calculateStats(data);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Gagal memuat milestone templates');
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const calculateStats = (data) => {
    let level0 = 0, level1 = 0, level2 = 0, groups = 0, leaves = 0, totalBobot = 0;
    
    const countRecursive = (items, level = 0) => {
      items.forEach(item => {
        if (level === 0) level0++;
        else if (level === 1) level1++;
        else if (level === 2) level2++;
        
        if (item.isGroup) groups++;
        else {
          leaves++;
          if (item.defaultBobot) totalBobot += item.defaultBobot;
        }
        
        if (item.children && item.children.length > 0) {
          countRecursive(item.children, level + 1);
        }
      });
    };
    
    countRecursive(data);
    
    setStats({
      totalMilestones: level0 + level1 + level2,
      level0Count: level0,
      level1Count: level1,
      level2Count: level2,
      groupCount: groups,
      leafCount: leaves,
      totalBobot: totalBobot
    });
  };

  // Filter templates based on search query
  const filterTemplates = () => {
    if (!searchQuery.trim()) {
      setFilteredTemplates(templates);
      return;
    }

    const query = searchQuery.toLowerCase();
    
    const filterRecursive = (items) => {
      return items.reduce((acc, item) => {
        const matchesSearch = 
          item.itemCode.toLowerCase().includes(query) ||
          item.milestoneName.toLowerCase().includes(query) ||
          (item.evidenceDescription && item.evidenceDescription.toLowerCase().includes(query));

        if (matchesSearch) {
          acc.push(item);
        } else if (item.children && item.children.length > 0) {
          const filteredChildren = filterRecursive(item.children);
          if (filteredChildren.length > 0) {
            acc.push({
              ...item,
              children: filteredChildren
            });
          }
        }
        
        return acc;
      }, []);
    };

    const filtered = filterRecursive(templates);
    setFilteredTemplates(filtered);
  };

  // Handle refresh
  const handleRefresh = () => {
    setSearchQuery('');
    loadTemplates();
    toast.success('Data refreshed');
  };

  // Handle project type change
  const handleProjectTypeChange = (type) => {
    setSelectedProjectType(type);
    setSearchQuery('');
  };

  // Handle configure evidence
  const handleConfigureEvidence = (milestone) => {
    setSelectedMilestone(milestone);
    setIsEvidenceModalOpen(true);
  };

  // Handle view milestone details
  const handleViewMilestone = (milestone) => {
    setSelectedMilestone(milestone);
    setIsViewModalOpen(true);
  };

  // Close evidence modal
  const closeEvidenceModal = () => {
    setIsEvidenceModalOpen(false);
    setSelectedMilestone(null);
  };

  // Close view modal
  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedMilestone(null);
  };

  // Handle evidence save (refresh data)
  const handleEvidenceSave = () => {
    loadTemplates(); // Reload to get updated evidence counts
  };

  // Flatten tree for flat view
  const flattenTree = (items, level = 0) => {
    let result = [];
    items.forEach(item => {
      result.push({ ...item, level });
      if (item.children && item.children.length > 0) {
        result = result.concat(flattenTree(item.children, level + 1));
      }
    });
    return result;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">📋 Milestone Template List</h1>
            <p className="text-gray-600 mt-1">Halaman untuk mengelola Template Milestone project</p>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by code, name, or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Project Type Tabs */}
        <div className="flex items-center space-x-2 flex-wrap">
          <Filter className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Project Type:</span>
          <div className="flex space-x-2 flex-wrap gap-2">
            {projectTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => handleProjectTypeChange(type.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedProjectType === type.value
                    ? `${type.color} text-white shadow-md`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {type.label}
                {selectedProjectType === type.value && (
                  <span className="ml-2 text-xs opacity-75">({type.description})</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Level 0 */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md p-4 text-white">
          <div className="text-sm opacity-90">Level 0 (Root)</div>
          <div className="text-3xl font-bold mt-1">{stats.level0Count}</div>
          <div className="text-xs opacity-75 mt-1">Root milestones</div>
        </div>

        {/* Level 1 */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md p-4 text-white">
          <div className="text-sm opacity-90">Level 1 (Child)</div>
          <div className="text-3xl font-bold mt-1">{stats.level1Count}</div>
          <div className="text-xs opacity-75 mt-1">Child milestones</div>
        </div>

        {/* Level 2 */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md p-4 text-white">
          <div className="text-sm opacity-90">Level 2 (Grandchild)</div>
          <div className="text-3xl font-bold mt-1">{stats.level2Count}</div>
          <div className="text-xs opacity-75 mt-1">Grandchild milestones</div>
        </div>

        {/* Total Bobot */}
        <div className={`bg-gradient-to-br rounded-lg shadow-md p-4 text-white ${
          stats.totalBobot === 100 ? 'from-green-500 to-green-600' : 'from-red-500 to-red-600'
        }`}>
          <div className="text-sm opacity-90">Total Bobot</div>
          <div className="text-3xl font-bold mt-1">{stats.totalBobot.toFixed(0)}%</div>
          <div className="text-xs opacity-75 mt-1">
            {stats.totalBobot === 100 ? '✓ Valid' : '⚠ Must be 100%'}
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Table Header with View Mode Toggle */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Milestone Templates ({stats.totalMilestones})
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {stats.groupCount} groups, {stats.leafCount} leaf milestones
              </p>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">View:</span>
              <button
                onClick={() => setViewMode('tree')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'tree'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Tree
              </button>
              <button
                onClick={() => setViewMode('flat')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'flat'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Flat
              </button>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
              <span className="ml-3 text-gray-600">Loading templates...</span>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No milestone templates found</p>
              {searchQuery && (
                <p className="text-gray-400 text-sm mt-2">Try adjusting your search query</p>
              )}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-red-600 to-blue-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider w-16">
                    No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Code & Milestone Name
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-20">
                    Level
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24">
                    Type
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24">
                    Bobot
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-32">
                    Calc Method
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24">
                    Evidence
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-28">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {viewMode === 'tree' ? (
                  // Tree View
                  filteredTemplates.map((template) => (
                    <MilestoneTreeRow
                      key={template.id}
                      milestone={template}
                      level={0}
                      onConfigureEvidence={handleConfigureEvidence}
                      onView={handleViewMilestone}
                    />
                  ))
                ) : (
                  // Flat View
                  flattenTree(filteredTemplates).map((template) => (
                    <MilestoneTreeRow
                      key={template.id}
                      milestone={{ ...template, children: [] }} // Remove children for flat view
                      level={template.level}
                      onConfigureEvidence={handleConfigureEvidence}
                      onView={handleViewMilestone}
                    />
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Table Footer */}
        {!loading && filteredTemplates.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div>
                Menampilkan {stats.totalMilestones} milestone templates
              </div>
              <div className="flex items-center space-x-2">
                {searchQuery && (
                  <span className="text-blue-600 font-medium">
                    Filtered results
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Evidence Configuration Modal */}
      <EvidenceConfigModal
        isOpen={isEvidenceModalOpen}
        onClose={closeEvidenceModal}
        milestone={selectedMilestone}
        onSave={handleEvidenceSave}
      />

      {/* View Milestone Details Modal */}
      {isViewModalOpen && selectedMilestone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-red-600">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Milestone Details</h2>
                <button
                  onClick={closeViewModal}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Item Code</label>
                    <p className="mt-1 text-lg font-semibold text-gray-900">{selectedMilestone.itemCode}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Project Type</label>
                    <p className="mt-1 text-lg font-semibold text-gray-900">{selectedMilestone.projectType}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">Milestone Name</label>
                  <p className="mt-1 text-lg font-semibold text-gray-900">{selectedMilestone.milestoneName}</p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Type</label>
                    <p className="mt-1 text-gray-900">{selectedMilestone.isGroup ? 'Group' : 'Leaf'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Level</label>
                    <p className="mt-1 text-gray-900">Level {selectedMilestone.level}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Default Bobot</label>
                    <p className="mt-1 text-gray-900">
                      {selectedMilestone.defaultBobot ? `${selectedMilestone.defaultBobot}%` : '-'}
                    </p>
                  </div>
                </div>

                {!selectedMilestone.isGroup && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Calculation Method</label>
                      <p className="mt-1 text-gray-900">
                        {selectedMilestone.calculationMethod || '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Volume Unit</label>
                      <p className="mt-1 text-gray-900">
                        {selectedMilestone.volumeUnit || '-'}
                      </p>
                    </div>
                  </div>
                )}

                {selectedMilestone.evidenceDescription && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Evidence Description</label>
                    <p className="mt-1 text-gray-900">{selectedMilestone.evidenceDescription}</p>
                  </div>
                )}

                {selectedMilestone.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Notes</label>
                    <p className="mt-1 text-gray-900">{selectedMilestone.notes}</p>
                  </div>
                )}

                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-600 mb-2">
                    Evidence Requirements ({selectedMilestone.evidenceDefinitions?.length || 0})
                  </label>
                  {selectedMilestone.evidenceDefinitions && selectedMilestone.evidenceDefinitions.length > 0 ? (
                    <div className="space-y-2">
                      {selectedMilestone.evidenceDefinitions.map((ev, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded border border-gray-200">
                          <div className="font-medium text-gray-900">{ev.evidenceName}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            {ev.evidenceType} • {ev.isMandatory ? 'Mandatory' : 'Optional'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No evidence requirements configured</p>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeViewModal}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilestoneTemplateList;